'use client'

import Link from 'next/link'
import { Sparkles, SearchX } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

type ActionLink = {
  label: string
  href: string
  variant?: React.ComponentProps<typeof Button>['variant']
}

export type NoResultsStateProps = {
  query?: string | null
  title?: string
  description?: string
  suggestions?: string[]
  primaryAction?: ActionLink
  secondaryAction?: ActionLink
  className?: string
}

const defaultSuggestions = [
  'Double-check spelling or trim the keywords.',
  'Tweak your filters (Premium/Free, asset type, tags) to widen the results.',
  'Browse another category - thousands of fresh assets are waiting.',
]

export function NoResultsState({
  query,
  title,
  description,
  suggestions = defaultSuggestions,
  primaryAction,
  secondaryAction,
  className,
}: NoResultsStateProps) {
  const resolvedTitle = title ?? "We couldn't find a perfect match"
  const resolvedDescription =
    description ??
    "The vault is definitely stocked. Give the search another angle and we'll surface something worthy of your next big shot."

  return (
    <div
      className={cn(
        'tw-relative tw-isolate tw-overflow-hidden tw-px-4 tw-py-12 tw-text-white tw-text-center sm:tw-px-10',
        className
      )}
    >
      <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_center,rgba(255,214,170,0.18),transparent_55%)] tw-blur-[140px] tw-opacity-90" />
      <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_bottom,rgba(251,191,36,0.08),transparent_60%)] tw-blur-3xl" />
      <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[linear-gradient(135deg,rgba(11,11,15,0.95),rgba(16,13,20,0.7),rgba(9,9,12,0.92))]" />

      <div className="tw-relative tw-mx-auto tw-flex tw-max-w-4xl tw-flex-col tw-items-center tw-gap-6 tw-text-center">
        <div className="tw-inline-flex tw-items-center tw-gap-3 tw-rounded-full tw-bg-white/10 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.35em] tw-text-white/80">
          <SearchX className="tw-h-4 tw-w-4" aria-hidden />
          <span>Search Radar</span>
        </div>

        <div className="tw-space-y-4">
          <h3 className="display_6 tw-text-balance tw-font-semibold tw-leading-tight tw-text-[2rem] md:tw-text-[2.6rem]">
            <span className="tw-text-white">{resolvedTitle}</span>
            {query && (
              <>
                {' '}
                for{' '}
                <span className="tw-inline-flex tw-rounded-full tw-bg-white/10 tw-px-3 tw-py-1 tw-font-bold tw-text-white">
                  &quot;{query}&quot;
                </span>
              </>
            )}
          </h3>
          <p className="body_2 tw-text-white/80">{resolvedDescription}</p>
        </div>

        {!!suggestions.length && (
          <ul className="tw-grid tw-w-full tw-gap-4 tw-text-left md:tw-grid-cols-3">
            {suggestions.map((suggestion) => (
              <li key={suggestion} className="tw-flex tw-items-start tw-gap-3 tw-text-sm tw-text-white/85">
                <Sparkles className="tw-mt-0.5 tw-h-4 tw-w-4 tw-flex-none tw-text-[#fbbf24]" aria-hidden />
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        )}

        {(primaryAction || secondaryAction) && (
          <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-pt-2 sm:tw-flex-row">
            {primaryAction && (
              <Button asChild variant={primaryAction.variant ?? 'default'}>
                <Link href={primaryAction.href}>{primaryAction.label}</Link>
              </Button>
            )}
            {secondaryAction && (
              <Button asChild variant={secondaryAction.variant ?? 'ghost'}>
                <Link href={secondaryAction.href}>{secondaryAction.label}</Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
